/**
 * G2eGente - Matriz de Polivalência
 * Visualização de competências: funcionários x cargos
 * Inclui geração automática de PDI baseado em gaps
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { skillsMatrixAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { 
  Loader2,
  Grid3X3,
  Users,
  Briefcase,
  CheckCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Filter,
  Info,
  GraduationCap,
  Star,
  FileText,
  Target,
  Clock,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Cores e labels para os status
const STATUS_CONFIG = {
  apto: {
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgLight: 'bg-green-100',
    label: 'Apto',
    icon: CheckCircle
  },
  parcial: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    bgLight: 'bg-yellow-100',
    label: 'Parcial',
    icon: AlertTriangle
  },
  desenvolvimento: {
    color: 'bg-orange-500',
    textColor: 'text-orange-700',
    bgLight: 'bg-orange-100',
    label: 'Em Desenvolvimento',
    icon: TrendingUp
  },
  nao_apto: {
    color: 'bg-slate-300',
    textColor: 'text-slate-500',
    bgLight: 'bg-slate-100',
    label: 'Não Apto',
    icon: XCircle
  }
};

export default function MatrizPolivalencia() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [filterSetor, setFilterSetor] = useState('all');
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Gaps/PDI modal
  const [showGapsModal, setShowGapsModal] = useState(false);
  const [selectedGap, setSelectedGap] = useState(null);
  const [gapsData, setGapsData] = useState(null);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const [generatingPDI, setGeneratingPDI] = useState(false);
  const [periodoMeses, setPeriodoMeses] = useState(6);

  useEffect(() => {
    fetchMatrix();
  }, [filterSetor]);

  const fetchMatrix = async () => {
    try {
      setLoading(true);
      const setor = filterSetor === 'all' ? null : filterSetor;
      const res = await skillsMatrixAPI.getMatrix(setor);
      setMatrixData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar matriz');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = async (employee, qualificacao) => {
    // Se não é apto, abrir modal de gaps
    if (qualificacao.status !== 'apto') {
      setSelectedGap({ employee, qualificacao });
      await loadGaps(employee.employee_id, qualificacao.cargo_id);
      setShowGapsModal(true);
    } else {
      // Se apto, mostrar detalhes normais
      setSelectedEmployee({ ...employee, qualificacao });
      setShowDetailModal(true);
      
      try {
        setLoadingDetail(true);
        const res = await skillsMatrixAPI.getEmployeeDetail(employee.employee_id);
        setEmployeeDetail(res.data);
      } catch (error) {
        toast.error('Erro ao carregar detalhes');
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const loadGaps = async (employeeId, cargoId) => {
    try {
      setLoadingGaps(true);
      const res = await skillsMatrixAPI.getGaps(employeeId, cargoId);
      setGapsData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar gaps');
    } finally {
      setLoadingGaps(false);
    }
  };

  const handleGeneratePDI = async () => {
    if (!selectedGap) return;
    
    try {
      setGeneratingPDI(true);
      const res = await skillsMatrixAPI.generatePDI(
        selectedGap.employee.employee_id,
        selectedGap.qualificacao.cargo_id,
        periodoMeses
      );
      
      if (res.data.success) {
        toast.success(`PDI criado com ${res.data.resumo.total_acoes} ações de desenvolvimento`);
        setShowGapsModal(false);
        // Oferecer navegação para o PDI
        if (res.data.pdi_id) {
          const goToPDI = window.confirm('PDI criado com sucesso! Deseja visualizá-lo agora?');
          if (goToPDI) {
            navigate('/company/pdi');
          }
        }
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar PDI');
    } finally {
      setGeneratingPDI(false);
    }
  };

  const getStatusCell = (qualificacao) => {
    const config = STATUS_CONFIG[qualificacao.status];
    const Icon = config.icon;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={`w-full h-full min-h-[40px] flex items-center justify-center cursor-pointer 
                ${config.bgLight} hover:opacity-80 transition-opacity relative`}
            >
              {qualificacao.is_cargo_atual && (
                <Star className="absolute top-1 right-1 w-3 h-3 text-amber-500 fill-amber-500" />
              )}
              <div className="flex flex-col items-center gap-0.5">
                <Icon className={`w-4 h-4 ${config.textColor}`} />
                <span className={`text-xs font-medium ${config.textColor}`}>
                  {qualificacao.percentual}%
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <p className="font-medium">{qualificacao.cargo_nome}</p>
              <p>{config.label} - {qualificacao.percentual}%</p>
              <p className="text-xs text-slate-400">
                Treinamentos: {qualificacao.treinamentos_ok}/{qualificacao.treinamentos_total}
                {qualificacao.treinamentos_vencidos > 0 && 
                  ` (${qualificacao.treinamentos_vencidos} vencido${qualificacao.treinamentos_vencidos > 1 ? 's' : ''})`
                }
              </p>
              {qualificacao.is_cargo_atual && (
                <p className="text-xs text-amber-500 font-medium">Cargo atual</p>
              )}
              {qualificacao.status !== 'apto' && (
                <p className="text-xs text-cyan-500 mt-1">Clique para ver gaps e gerar PDI</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="skills-matrix-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Matriz de Polivalência</h1>
            <p className="text-slate-600">Visualize a qualificação dos funcionários para cada cargo</p>
          </div>
          
          {matrixData?.setores?.length > 0 && (
            <Select value={filterSetor} onValueChange={setFilterSetor}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por setor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os setores</SelectItem>
                {matrixData.setores.map((setor) => (
                  <SelectItem key={setor} value={setor}>{setor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Users className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {matrixData?.stats?.total_funcionarios || 0}
                  </p>
                  <p className="text-sm text-slate-600">Funcionários</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {matrixData?.stats?.total_cargos || 0}
                  </p>
                  <p className="text-sm text-slate-600">Cargos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Grid3X3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {matrixData?.stats?.media_polivalencia || 0}
                  </p>
                  <p className="text-sm text-slate-600">Média de Cargos/Func.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded ${config.color}`}></div>
                    <span className="text-xs text-slate-600">{config.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                <Star className="w-3 h-3 inline text-amber-500 fill-amber-500" /> = Cargo atual
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-purple-50 to-cyan-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Geração Automática de PDI</p>
                <p className="text-sm text-slate-600">
                  Clique em uma célula amarela, laranja ou cinza para ver os gaps de treinamento 
                  e gerar automaticamente um Plano de Desenvolvimento Individual (PDI) com as ações necessárias.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Matrix */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-cyan-600" />
              Matriz de Competências
            </CardTitle>
            <CardDescription>
              Clique em uma célula para ver detalhes ou gerar PDI
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {matrixData?.matrix?.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Nenhum funcionário encontrado
              </div>
            ) : matrixData?.cargos?.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Nenhum cargo cadastrado. Cadastre cargos em Engenharia de Cargo.
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-auto max-h-[65vh]" style={{ scrollbarWidth: 'thin' }}>
                <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="sticky left-0 bg-slate-50 z-10 min-w-[200px]">
                      Funcionário
                    </TableHead>
                    {matrixData?.cargos?.map((cargo) => (
                      <TableHead 
                        key={cargo.id} 
                        className="text-center min-w-[100px] max-w-[150px]"
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="truncate cursor-help">
                                {cargo.titulo}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <p className="font-medium">{cargo.titulo}</p>
                              {cargo.setor && <p className="text-xs text-slate-400">Setor: {cargo.setor}</p>}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrixData?.matrix?.map((row) => (
                    <TableRow key={row.employee_id}>
                      <TableCell className="sticky left-0 bg-white z-10 font-medium">
                        <div>
                          <p className="text-slate-900">{row.employee_nome}</p>
                          <p className="text-xs text-slate-500">
                            {row.employee_cargo_atual}
                            {row.employee_setor && ` • ${row.employee_setor}`}
                          </p>
                        </div>
                      </TableCell>
                      {row.qualificacoes.map((q) => (
                        <TableCell 
                          key={q.cargo_id} 
                          className="p-0"
                          onClick={() => handleCellClick(row, q)}
                        >
                          {getStatusCell(q)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gaps/PDI Modal */}
        <Dialog open={showGapsModal} onOpenChange={setShowGapsModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-600" />
                Gaps de Treinamento e PDI
              </DialogTitle>
              <DialogDescription>
                {selectedGap?.employee?.employee_nome} → {selectedGap?.qualificacao?.cargo_nome}
              </DialogDescription>
            </DialogHeader>

            {loadingGaps ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : gapsData ? (
              <div className="space-y-6 py-4">
                {/* Status geral */}
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Conformidade Atual</p>
                      <p className="text-sm text-slate-600">
                        {gapsData.total_atendidos} de {gapsData.total_requisitos} requisitos atendidos
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-slate-900">
                        {gapsData.percentual_conformidade}%
                      </p>
                    </div>
                  </div>
                  <Progress value={gapsData.percentual_conformidade} className="mt-3" />
                </div>

                {/* PDI existente */}
                {gapsData.pdi_existente && (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-900">PDI já existe</p>
                          <p className="text-sm text-amber-700">{gapsData.pdi_existente.titulo}</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        {gapsData.pdi_existente.status}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Treinamentos Vencidos */}
                {gapsData.treinamentos_vencidos?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Treinamentos Vencidos (Reciclagem Urgente)
                    </h4>
                    <div className="space-y-2">
                      {gapsData.treinamentos_vencidos.map((t, idx) => (
                        <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{t.nome}</p>
                              <p className="text-xs text-slate-600">
                                {t.requisitos_legais && <span className="text-red-600">{t.requisitos_legais} • </span>}
                                Vencido há {Math.abs(t.dias_para_vencer)} dias
                              </p>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Vencido</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Treinamentos Vencendo */}
                {gapsData.treinamentos_vencendo?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-amber-600 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Treinamentos Vencendo em Breve
                    </h4>
                    <div className="space-y-2">
                      {gapsData.treinamentos_vencendo.map((t, idx) => (
                        <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{t.nome}</p>
                              <p className="text-xs text-slate-600">
                                Vence em {t.dias_para_vencer} dias
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">Vencendo</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Treinamentos Faltantes */}
                {gapsData.treinamentos_faltantes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-cyan-600" />
                      Treinamentos Pendentes
                    </h4>
                    <div className="space-y-2">
                      {gapsData.treinamentos_faltantes.map((t, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{t.nome}</p>
                              <p className="text-xs text-slate-600">
                                {t.requisitos_legais && <span className="text-purple-600">{t.requisitos_legais} • </span>}
                                {t.carga_horaria && `${t.carga_horaria}h • `}
                                {t.fornecedor || 'Fornecedor não definido'}
                              </p>
                            </div>
                            {t.obrigatorio && (
                              <Badge variant="outline" className="text-purple-600">Obrigatório</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Treinamentos OK */}
                {gapsData.treinamentos_ok?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Treinamentos em Dia ({gapsData.treinamentos_ok.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {gapsData.treinamentos_ok.map((t, idx) => (
                        <Badge key={idx} className="bg-green-100 text-green-700">{t.nome}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gerar PDI */}
                {gapsData.pode_gerar_pdi && !gapsData.pdi_existente && (
                  <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Gerar PDI Automático
                    </h4>
                    <p className="text-sm text-purple-700 mb-4">
                      Crie automaticamente um Plano de Desenvolvimento Individual com ações para cada 
                      treinamento pendente ou vencido.
                    </p>
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label htmlFor="periodo">Período do PDI (meses)</Label>
                        <Select value={periodoMeses.toString()} onValueChange={(v) => setPeriodoMeses(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 meses</SelectItem>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        onClick={handleGeneratePDI}
                        disabled={generatingPDI}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {generatingPDI ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        Gerar PDI
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      Serão criadas {(gapsData.treinamentos_faltantes?.length || 0) + (gapsData.treinamentos_vencidos?.length || 0)} ações de desenvolvimento
                    </p>
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGapsModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-600" />
                Detalhes de Qualificação
              </DialogTitle>
              <DialogDescription>
                {selectedEmployee?.employee_nome} - {selectedEmployee?.qualificacao?.cargo_nome}
              </DialogDescription>
            </DialogHeader>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : employeeDetail ? (
              <div className="space-y-6 py-4">
                {/* Status geral */}
                {selectedEmployee?.qualificacao && (
                  <div className={`p-4 rounded-lg ${STATUS_CONFIG[selectedEmployee.qualificacao.status].bgLight}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {React.createElement(STATUS_CONFIG[selectedEmployee.qualificacao.status].icon, {
                          className: `w-6 h-6 ${STATUS_CONFIG[selectedEmployee.qualificacao.status].textColor}`
                        })}
                        <div>
                          <p className={`font-semibold ${STATUS_CONFIG[selectedEmployee.qualificacao.status].textColor}`}>
                            {STATUS_CONFIG[selectedEmployee.qualificacao.status].label}
                          </p>
                          <p className="text-sm text-slate-600">
                            Treinamentos: {selectedEmployee.qualificacao.treinamentos_ok}/{selectedEmployee.qualificacao.treinamentos_total}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-slate-900">
                          {selectedEmployee.qualificacao.percentual}%
                        </p>
                        <p className="text-xs text-slate-500">Qualificação</p>
                      </div>
                    </div>
                    <Progress 
                      value={selectedEmployee.qualificacao.percentual} 
                      className="mt-3"
                    />
                  </div>
                )}

                {/* Treinamentos realizados */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-cyan-600" />
                    Treinamentos Realizados ({employeeDetail.total_treinamentos})
                  </h4>
                  {employeeDetail.treinamentos_realizados.length === 0 ? (
                    <p className="text-sm text-slate-500">Nenhum treinamento registrado</p>
                  ) : (
                    <div className="space-y-2">
                      {employeeDetail.treinamentos_realizados.map((t, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{t.nome}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(t.data_realizacao).toLocaleDateString('pt-BR')}
                              {t.data_validade && ` • Válido até ${new Date(t.data_validade).toLocaleDateString('pt-BR')}`}
                            </p>
                          </div>
                          <Badge className={
                            t.status === 'valido' ? 'bg-green-100 text-green-700' :
                            t.status === 'vencendo' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {t.status === 'valido' ? 'Válido' :
                             t.status === 'vencendo' ? `Vence em ${t.dias_para_vencer}d` :
                             'Vencido'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cargos compatíveis */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-600" />
                    Cargos Compatíveis
                  </h4>
                  <div className="space-y-2">
                    {employeeDetail.cargos_compativeis.slice(0, 5).map((c, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{c.cargo_nome}</p>
                          <p className="text-xs text-slate-500">
                            Treinamentos: {c.treinamentos_ok}/{c.treinamentos_total}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={c.percentual} className="w-20" />
                          <span className={`text-sm font-medium ${
                            c.percentual >= 100 ? 'text-green-600' :
                            c.percentual >= 70 ? 'text-yellow-600' :
                            'text-slate-600'
                          }`}>
                            {c.percentual}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
